import { useNavigate, useParams } from '@tanstack/react-router';
import bbox from '@turf/bbox';
import distance from '@turf/distance';
import { Layer, Source } from '@vis.gl/react-maplibre';
import type { ExpressionSpecification, GeoJSONSource, MapLayerEventType } from 'maplibre-gl';
import { Fragment, useCallback } from 'react';

import { LayerHoverCursorStyle } from '@/components/report/map/LayerHoverCursorStyle';
import { useIsBackend } from '@/hooks/useIsBackend';
import { useMapLayerOnEvent } from '@/hooks/useMapLayerOnEvent';
import { useScreenSize } from '@/hooks/useScreenSize';
import { ReportMainCategory, ReportStatusType } from '@/modules/api';
import { useCreateReportStore } from '@/stores/useCreateReportStore';
import { useMapStore } from '@/stores/useMapStore';
import { LocationInputMode } from '@/types/LocationInputMode';
import type { ReportFeature, ReportsFeatureCollection } from '@/types/ReportFeature';

export const REPORTS_SOURCE = 'reports';
export const REPORTS_CLUSTER_CIRCLE_LAYER = `${REPORTS_SOURCE}-cluster-circle-layer`;
const REPORTS_CLUSTER_COUNT_LAYER = `${REPORTS_SOURCE}-cluster-count-layer`;
const REPORTS_ICON_LAYER = `${REPORTS_SOURCE}-icon-layer`;

interface ReportsLayerProps {
  data: string | ReportsFeatureCollection;
  fitBoundsHorizontalPaddingFactor: number;
}

export function ReportsLayer({ data, fitBoundsHorizontalPaddingFactor }: ReportsLayerProps) {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const { isMobile } = useScreenSize();

  const reportStatuses = useMapStore((state) => state.reportStatuses);
  const padding = useMapStore((state) => state.padding);

  const locationInputMode = useCreateReportStore((state) => state.locationInputMode);

  const { isBackend } = useIsBackend();

  const handleIconClick = useCallback(
    async ({ features }: MapLayerEventType['click']) => {
      if (locationInputMode === LocationInputMode.MAP) {
        return;
      }

      const reportId = features?.[0]?.id;
      if (typeof reportId === 'string') {
        await navigate(
          isBackend
            ? {
                to: '/verwaltung/{-$adminLevelId}/meldungen/$reportId',
                params: {
                  reportId,
                  adminLevelId: params.adminLevelId,
                },
              }
            : {
                to: '/meldungen/$reportId',
                params: {
                  reportId,
                },
                search: {
                  laenge: undefined,
                  breite: undefined,
                  grenze: undefined,
                  zoom: undefined,
                },
              },
        );
      }
    },
    [isBackend, locationInputMode, navigate, params.adminLevelId],
  );

  useMapLayerOnEvent({ type: 'click', layer: REPORTS_ICON_LAYER, listener: handleIconClick });

  const handleClusterClick = useCallback(
    async ({ target, features }: MapLayerEventType['click']) => {
      if (locationInputMode === LocationInputMode.MAP) {
        return;
      }

      const clusterId = features?.[0]?.properties?.cluster_id;
      const clusterGeometry = features?.[0]?.geometry;

      if (clusterGeometry?.type !== 'Point') {
        return;
      }

      const leaves =
        ((await target
          .getSource<GeoJSONSource>(REPORTS_SOURCE)
          ?.getClusterLeaves(clusterId, 1000000, 0)) as ReportFeature[]) ?? [];

      const routeSignLocationId = leaves?.[0]?.properties.routeSignLocationId;

      if (
        routeSignLocationId &&
        leaves.every((report) => report.properties.routeSignLocationId === routeSignLocationId)
      ) {
        await navigate({
          to: '/beschilderung/$routeSignLocationId',
          params: { routeSignLocationId },
          search: { details: true },
        });
      } else if (
        leaves.length > 1 &&
        (target.getZoom() >= target.getMaxZoom() ||
          leaves.every(({ geometry }) => distance(clusterGeometry, geometry, { units: 'meters' }) <= 4))
      ) {
        await navigate(
          isBackend
            ? {
                to: '/verwaltung/{-$adminLevelId}/meldungen/auswahl',
                params: {
                  adminLevelId: params.adminLevelId,
                },
                search: {
                  laenge: parseFloat((clusterGeometry.coordinates[0] as number).toFixed(6)),
                  breite: parseFloat((clusterGeometry.coordinates[1] as number).toFixed(6)),
                  cluster: clusterId,
                },
              }
            : {
                to: '/meldungen/auswahl',
                search: {
                  laenge: parseFloat((clusterGeometry.coordinates[0] as number).toFixed(6)),
                  breite: parseFloat((clusterGeometry.coordinates[1] as number).toFixed(6)),
                  cluster: clusterId,
                },
              },

          // href: `${basePath}/meldungen/auswahl?laenge=${clusterGeometry.coordinates[0]?.toFixed(
          //   6,
          // )}&breite=${clusterGeometry.coordinates[1]?.toFixed(6)}&cluster=${clusterId}`,
        );
      } else {
        const bounds = bbox({
          type: 'FeatureCollection',
          features: leaves,
        }) as [number, number, number, number];

        const { width, height } = target.getCanvasContainer().getBoundingClientRect();

        target.fitBounds(bounds, {
          padding: isMobile
            ? { top: 72 + 24 + 32, right: 24, bottom: padding.bottom + 24, left: 24 }
            : {
                top: Math.max(64 + 88, height * 0.1 + 88),
                right: width * fitBoundsHorizontalPaddingFactor + 32,
                bottom: Math.max(64, height * 0.1),
                left: width * fitBoundsHorizontalPaddingFactor + 32,
              },
        });
      }
    },
    [
      fitBoundsHorizontalPaddingFactor,
      isBackend,
      isMobile,
      locationInputMode,
      navigate,
      padding.bottom,
      params.adminLevelId,
    ],
  );

  useMapLayerOnEvent({
    type: 'click',
    layer: REPORTS_CLUSTER_CIRCLE_LAYER,
    listener: handleClusterClick,
  });

  const activeReportStatuses = (Object.entries(reportStatuses) as unknown as [ReportStatusType, boolean][]).reduce<
    ReportStatusType[]
  >((active, [reportStatus, value]) => {
    if (value && !(reportStatus === ReportStatusType.ARCHIVED && !isBackend)) {
      active.push(reportStatus);
    }

    return active;
  }, []);

  return (
    <Fragment>
      <LayerHoverCursorStyle layers={[REPORTS_CLUSTER_CIRCLE_LAYER, REPORTS_ICON_LAYER]} />

      <Source
        key={activeReportStatuses.join('-')}
        cluster
        clusterMaxZoom={22}
        clusterRadius={40}
        data={data}
        filter={['in', ['get', 'status'], ['literal', activeReportStatuses]]}
        id={REPORTS_SOURCE}
        promoteId="id"
        type="geojson"
      >
        <Layer
          filter={['has', 'point_count']}
          id={REPORTS_CLUSTER_CIRCLE_LAYER}
          type="symbol"
          layout={{
            'icon-image': 'mpris:report-cluster-circle',
            'icon-allow-overlap': true,
            'icon-size': ['step', ['get', 'point_count'], 1 / 1.5, 100, 1],
            'icon-offset': [0, 9],
          }}
        />
        <Layer
          filter={['has', 'point_count']}
          id={REPORTS_CLUSTER_COUNT_LAYER}
          type="symbol"
          layout={{
            'text-allow-overlap': true,
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Noto Sans Regular'],
            'text-size': 12,
          }}
          paint={{
            'text-color': 'white',
          }}
        />

        <Layer
          id={REPORTS_ICON_LAYER}
          type="symbol"
          filter={[
            'all',
            ['!', ['has', 'point_count']],
            ...(params.reportId ? [['!=', ['get', 'id'], params.reportId] satisfies ExpressionSpecification] : []),
          ]}
          layout={{
            'icon-image': [
              'case',
              ['==', ['get', 'mainCategory', ['get', 'category']], ReportMainCategory.LIKE],
              'mpris:report-like',
              ['concat', 'mpris:report-', ['join', ['split', ['downcase', ['get', 'status']], '_'], '-']],
            ],
            'icon-anchor': 'bottom',
            'icon-offset': [0, 10],
            'icon-allow-overlap': true,
          }}
        />
      </Source>
    </Fragment>
  );
}
